import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, Grid, Typography, Form, TextButton } from 'bonfire-kindling';

import { NumEmployees, Roles } from 'constants/settings';
import { createFormValidator } from 'helpers/validation';
import { pickVendorBusinessInfoFields, useVendor, useTranslatedVendorSchema } from 'helpers/vendor';
import { useFeatureFlags } from 'helpers/hooks';
import { CountrySelect, SubregionAutocomplete } from 'components';

const BusinessInfo = ({ onSubmit: _onSubmit }) => {
  const { t } = useTranslation();
  const schema = useTranslatedVendorSchema();
  const { data: vendor } = useVendor();
  const { isFlagEnabled } = useFeatureFlags();

  const initialValues = useMemo(() => pickVendorBusinessInfoFields(vendor), [vendor]);

  const validate = useMemo(() => createFormValidator(schema), [schema]);

  const onSubmit = (values, formApi) => {
    const fields = formApi.getRegisteredFields();
    const payload = fields.reduce((pay, fieldName) => {
      pay[fieldName] = values[fieldName] || '';
      return pay;
    }, {});
    const errors = validate(payload);
    return errors ?? _onSubmit(payload);
  };

  return (
    <>
      <Typography variant="h1" component="h2" gutterBottom>
        {t('settings.business.title')}
      </Typography>
      <Box sx={{ maxWidth: { xl: '50%' } }}>
        <Form onSubmit={onSubmit} initialValues={initialValues}>
          {({ handleSubmit, submitting, values }) => (
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', xl: 'row' },
                  mb: 3,
                }}
              >
                <Box sx={{ mr: { xs: 0, xl: 3 } }}>
                  <Grid container columnSpacing={3}>
                    <Grid item xs={12}>
                      <Form.TextField
                        label={t('settings.business.vendor_contact_organization_name')}
                        name="vendorContactOrganizationName"
                        margin="dense"
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Form.TextField
                        label={t('settings.business.description')}
                        placeholder={t('settings.business.description_placeholder')}
                        name="businessDescription"
                        margin="dense"
                        multiline
                        required
                        rows={3}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Form.SelectField
                        label={t('settings.business.num_employees')}
                        name="numEmployees"
                        margin="dense"
                        fullWidth
                        required
                      >
                        {NumEmployees.map(({ value, label }) => (
                          <MenuItem key={value} value={value} label={label} />
                        ))}
                      </Form.SelectField>
                    </Grid>
                    <Grid item xs={12}>
                      <Form.SelectField
                        label={t('settings.business.your_role')}
                        name="role"
                        margin="dense"
                        fullWidth
                        required
                      >
                        {Roles.map(({ value, label }) => (
                          <MenuItem key={value} value={value} label={label} />
                        ))}
                      </Form.SelectField>
                    </Grid>
                    <Grid item xs={6}>
                      <Form.TextField
                        label={t('settings.business.vendor_contact_first_name')}
                        margin="dense"
                        name="vendorContactFirstName"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Form.TextField
                        label={t('settings.business.vendor_contact_last_name')}
                        margin="dense"
                        name="vendorContactLastName"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Form.TextField
                        label={t('settings.business.vendor_contact_email')}
                        margin="dense"
                        name="vendorContactEmail"
                        type="email"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Form.TextField
                        label={t('settings.business.phone_number')}
                        name="phoneNumber"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Grid container rowSpacing={0} columnSpacing={3}>
                    <Grid item xs={6}>
                      <Form.TextField
                        label={t('settings.business.address_line_1')}
                        name="addressLine1"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Form.TextField
                        label={t('settings.business.address_line_2')}
                        name="addressLine2"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Form.TextField
                        label={t('settings.business.address_line_3')}
                        name="addressLine3"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Form.TextField
                        label={t('settings.business.address_line_4')}
                        name="addressLine4"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Form.TextField
                        label={t('settings.business.city')}
                        name="city"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      {isFlagEnabled('FF_23_RELEASE_TOGGLE_VENDOR_COUNTRY_SELECT') ? (
                        <Form.Field name="region">
                          {({ input }) => (
                            <SubregionAutocomplete country={values.country} {...input} />
                          )}
                        </Form.Field>
                      ) : (
                        <Form.TextField
                          label={t('settings.business.region')}
                          name="region"
                          margin="dense"
                          fullWidth
                        />
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Form.TextField
                        label={t('settings.business.postal_or_zip_code')}
                        name="postalCode"
                        margin="dense"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      {isFlagEnabled('FF_23_RELEASE_TOGGLE_VENDOR_COUNTRY_SELECT') ? (
                        <Form.Field name="country">
                          {({ input }) => <CountrySelect {...input} />}
                        </Form.Field>
                      ) : (
                        <Form.TextField
                          label={t('settings.business.country')}
                          name="country"
                          margin="dense"
                          fullWidth
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <TextButton
                type="submit"
                variant="contained"
                color="primary"
                isLoading={submitting}
                label={t('settings.save')}
              />
            </form>
          )}
        </Form>
      </Box>
    </>
  );
};

export default BusinessInfo;
